/**
 * @file CartLookPanel.tsx
 * @description Панель «Вид корзины» конструктора сайта (phase19.16, партия
 * SB120, этап 4; uiux-cart §3 вариант «в», db-design-sb120-stage4 §2).
 *
 * Раздел `theme.cart` документа оформления: правка пишется темой целиком
 * через `useThemeDraft.update("cart", …)` с автосохранением (часть THEME,
 * конфликт — по версии части); ползунки показываются на канве живьём
 * (`useThemePreview`) и записываются по отпусканию. Без права THEME — только
 * просмотр (`CartLookFields readOnly`). Пока черновик грузится — скелет групп
 * и «Загружаем вид корзины…».
 */

import type { SiteCart } from "@chaiberi/shared";
import { useCallback } from "react";

import { Skeleton } from "../../ui/skeleton";
import { SaveIndicator } from "../SaveIndicator";
import { useSiteView } from "../site-view-context";
import { UnsavedGuard } from "../UnsavedGuard";
import { CartLookFields } from "./CartLookFields";
import { useSiteDraftPart } from "../../../hooks/site-view/useSiteDraftParts";
import { useThemePreview } from "../../../hooks/site-view/useThemePreview";
import { cartOf } from "../../../lib/site-view/cart-look";

export function CartLookPanel() {
  const view = useSiteView();
  if (!view.parts) return <CartLookSkeleton />;
  return <CartLookBody canEdit={view.editableParts.includes("THEME")} />;
}

function CartLookBody({ canEdit }: { canEdit: boolean }) {
  const themeDraft = useSiteDraftPart("theme");
  const { theme, update } = themeDraft;
  const cart = cartOf(theme);
  const showOnCanvas = useThemePreview(theme);
  // Раздел целиком: у старого сервера (без раздела) запись всё равно полная.
  const onChange = useCallback((patch: Partial<SiteCart>) => update("cart", { ...cart, ...patch }), [update, cart]);
  const onPreview = useCallback((patch: Partial<SiteCart>) => showOnCanvas("cart", { ...cart, ...patch }), [showOnCanvas, cart]);
  return (
    <div className="flex min-w-0 flex-col gap-4">
      {canEdit ? <SaveIndicator state={themeDraft} /> : null}
      <CartLookFields cart={cart} theme={theme} readOnly={!canEdit} onChange={onChange} onPreview={onPreview} />
      <UnsavedGuard when={themeDraft.status === "error" || themeDraft.status === "conflict"} />
    </div>
  );
}

function CartLookSkeleton() {
  return (
    <div className="flex min-w-0 flex-col gap-4" aria-busy="true">
      <p className="type-caption text-on-surface-variant">Загружаем вид корзины…</p>
      {[0, 1, 2, 3].map((i) => (
        <Skeleton key={i} className="h-16 rounded-xl" />
      ))}
    </div>
  );
}
